import { useTranslations } from '@vocab/react';
import { Box, Button, Hidden, IconAI, Stack } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { sendHotJarEvent } from 'src/hooks/useHotjarEvent';
import {
  useIsHomepageGenAIChatDecardWidgetEnabled,
  useIsHomepageGenAIChatDesktopDrawerEnabled,
} from 'src/store/featureFlags/hooks';

import translations from '../.vocab';
import { CareerFeedAIAssistantContentFrame } from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantContentFrame';
import { PrivacyAndCollectionNotice } from '../CareerFeedAIAssistantContent/PrivacyAndCollectionNotice';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import { useRecommendationForCareerFeedAiAssistant } from '../hooks/useRecommendationForCareerFeedAiAssistant';
import type { ErrorType } from '../types';

import SocialProofSection from './SocialProofSection/SocialProofSection';

const CareerFeedAiAssistantInitialStateView = ({
  isLoading,
  errorType,
  onMobileButtonClick,
  onDesktopButtonClick,
}: {
  isLoading: boolean;
  errorType?: ErrorType | null;
  onMobileButtonClick: () => void;
  onDesktopButtonClick: () => void;
  openDrawer?: boolean;
}) => {
  const { t } = useTranslations(translations);

  const { reset, openDesktopDrawer, setOpenDesktopDrawer } =
    useCareerFeedAIAssistant();

  const handleOnClose = useCallback(() => {
    reset();
    setOpenDesktopDrawer(false);
  }, [reset, setOpenDesktopDrawer]);

  const isHomepageGenAIChatDesktopDrawerEnabled =
    useIsHomepageGenAIChatDesktopDrawerEnabled();

  const isHomepageGenAIChatDecardWidgetEnabled =
    useIsHomepageGenAIChatDecardWidgetEnabled();

  return isHomepageGenAIChatDesktopDrawerEnabled ? (
    <CareerFeedAIAssistantContentFrame
      widgetHeadMessage={{
        text: t(
          'Answer a few questions so we can find the right jobs for you.',
        ),
        size: 'standard',
      }}
      widgetContentSection={null}
      widgetCtaSection={
        <Stack space="small">
          <Box maxWidth="content">
            <Hidden below="tablet">
              <Button
                tone="brandAccent"
                icon={<IconAI />}
                loading={false}
                onClick={onDesktopButtonClick}
              >
                {t('Refine recommendations')}
              </Button>
            </Hidden>
            <Hidden above="mobile">
              <Button
                tone="brandAccent"
                icon={<IconAI />}
                loading={isLoading}
                onClick={onMobileButtonClick}
              >
                {t('Refine recommendations')}
              </Button>
            </Hidden>
          </Box>
        </Stack>
      }
      errorType={errorType}
      drawerContentSection={<Box height="full" paddingY="xxlarge" />}
      shouldOpenDrawer={openDesktopDrawer}
      onDrawerClose={handleOnClose}
      socialProofSection={<SocialProofSection />}
      loadingOnWidget={isLoading && !openDesktopDrawer}
      loadingOnDrawer={isLoading}
    />
  ) : (
    <CareerFeedAIAssistantContentFrame
      widgetHeadMessage={{
        text: t(
          'Answer a few questions so we can find the right jobs for you.',
        ),
        size: 'standard',
      }}
      widgetContentSection={null}
      widgetCtaSection={
        <Stack space="small">
          <Box maxWidth="content">
            <Button
              tone="brandAccent"
              icon={<IconAI />}
              loading={isLoading}
              onClick={onMobileButtonClick}
            >
              {t('Refine recommendations')}
            </Button>
          </Box>
          {!isHomepageGenAIChatDecardWidgetEnabled && (
            <PrivacyAndCollectionNotice />
          )}
        </Stack>
      }
      errorType={errorType}
      socialProofSection={<SocialProofSection />}
      loadingOnWidget={isLoading}
      loadingOnDrawer={false}
    />
  );
};

export const CareerFeedAiAssistantInitialState = () => {
  const {
    invokeCreateSession,
    isCreateSessionLoading,
    createSessionError,
    tracking: { trackGenAIChatStartPressed, trackGenAIChatCtaDisplayed },
    setOpenDesktopDrawer,
  } = useCareerFeedAIAssistant();
  const { jobIds: recommendedJobIds } =
    useRecommendationForCareerFeedAiAssistant();

  const startSession = useCallback(() => {
    if (!isCreateSessionLoading) {
      trackGenAIChatStartPressed();
      invokeCreateSession(recommendedJobIds);
    }
  }, [
    invokeCreateSession,
    isCreateSessionLoading,
    recommendedJobIds,
    trackGenAIChatStartPressed,
  ]);

  const startSessionMobile = useCallback(() => {
    startSession();
  }, [startSession]);

  const startSessionDesktop = useCallback(() => {
    startSession();
    setOpenDesktopDrawer(true);
  }, [startSession, setOpenDesktopDrawer]);

  const { ref: trackedComponent, inView: isInView } = useInView({
    delay: 500,
    threshold: 0.5,
  });
  const [isCtaDisplayedEventFired, setIsCtaDisplayedEventFired] =
    useState(false);

  useEffect(() => {
    if (isInView && !isCtaDisplayedEventFired) {
      trackGenAIChatCtaDisplayed();
      sendHotJarEvent('homepage_genAI_start');
      setIsCtaDisplayedEventFired(true);
    }
  }, [isCtaDisplayedEventFired, isInView, trackGenAIChatCtaDisplayed]);

  return (
    <Box ref={trackedComponent}>
      <CareerFeedAiAssistantInitialStateView
        isLoading={isCreateSessionLoading}
        errorType={createSessionError}
        onMobileButtonClick={startSessionMobile}
        onDesktopButtonClick={startSessionDesktop}
      />
    </Box>
  );
};
